import React from 'react';
import styled from 'styled-components';

import { UsersIcon, FlagIcon } from './Icons';
import RowHighlighter from './RowHighlighter';

const externalLinkIcon = '\uE202';

const Wrapper = styled.div<{ active: boolean }>`
  ${({ active }) => active && 'margin-bottom: 4px'};
`;

const RowWrapper = styled.div`
  font-size: 12px;
  padding: 4px 0;
  display: grid;
  grid-template-columns: 300px 1fr 1fr;
`;

const IconWrapper = styled.div`
  text-align: right;
`;

const TextCell = styled.p`
  margin: auto 0;
  white-space: pre-wrap;
`;

type InternalNote = {
  body: string;
  flagged: boolean;
  timestamp: number;
  userFirstName: string;
  userLastName: string;
  userId: string;
};

type LedgerListProps = {
  ledgerAccounts: Record<string, unknown>[];
  baseUrl: string;
  urlSuffix: string;
  onSidebarToggle?: (data: { journalEntryId?: string; ledgerId?: string }) => void;
};

const hasFlaggedInternalNotes = (notes: Array<InternalNote>) => notes.some(note => note.flagged);
const hasInternalNotes = (notes: Array<InternalNote>) => notes.length > 0;

const LedgerList = ({ ledgerAccounts, baseUrl, urlSuffix, onSidebarToggle }: LedgerListProps) => (
  <Wrapper active={ledgerAccounts.length > 0}>
    {ledgerAccounts.map(({ id, name, details, internalNotes }) => {
      const hasNotes = hasInternalNotes(internalNotes);
      const hasFlaggedNotes = hasFlaggedInternalNotes(internalNotes);

      const onRowClick = () => {
        if (onSidebarToggle) {
          onSidebarToggle({ ledgerId: id });
        }
      };

      return (
        <RowHighlighter
          onRowClick={onRowClick}
          externalLink={baseUrl && `${baseUrl}/${id}${urlSuffix}`}
          externalLinkIcon={externalLinkIcon}
          key={id}
          data-test-id="ledger-list-item"
        >
          <RowWrapper>
            <TextCell>{name}</TextCell>
            <TextCell>{details}</TextCell>
            <IconWrapper>
              {hasNotes && (hasFlaggedNotes ? <FlagIcon /> : <UsersIcon />)}
            </IconWrapper>
          </RowWrapper>
        </RowHighlighter>
      );
    })}
  </Wrapper>
);

LedgerList.defaultProps = {
  ledgerAccounts: [],
  baseUrl: '',
  urlSuffix: '',
  onSidebarToggle: undefined,
};

export default LedgerList;
